import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { Button, Modal, FormInput, FormSelect, MultiSelect, EmptyState, Badge } from '../../components/common/Common';
import { Plus, Pencil, Trash2, Power, Play, Eye, RefreshCw, Recycle, Inbox } from 'lucide-react';
import { useCRM } from '../../context/CRMContext';
import { useToast } from '../../context/ToastContext';
import { apiFetch } from '../../utils/api';
import './RecycleLeads.css';

const EMPTY_FORM = {
  rule_name: '',
  trigger_status: [],
  duration_value: 48,
  duration_unit: 'hrs',
  new_status_lsid: '',
  dump_status_lsid: '',
  rotation_mode: 'manual',
  auto_assign: true,
  include_followup: false,
  reset_activity_min: 0,
  max_rotations: 0,
  cooldown_min: 0,
  pool_mode: 'users',
  reports_to_uid: '',
  users: [],
  is_active: true,
};

const ROTATION_MODES = [
  { value: 'manual', label: 'Manual (round-robin order)' },
  { value: 'random', label: 'Random' },
  { value: 'balanced', label: 'Balanced (least busy first)' },
];

const RecycleLeads = () => {
  const navigate = useNavigate();
  const { fetchUsers } = useCRM();
  const { showToast } = useToast();

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [running, setRunning] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');

  const [statuses, setStatuses] = useState([]);
  const statusOptions = statuses.map(s => ({ value: s.ls_id, label: s.name }));
  const statusName = (lsid) => statuses.find(s => String(s.ls_id) === String(lsid))?.name || '—';
  const userName = (uid) => {
    const u = users.find(x => String(x.u_id) === String(uid));
    return u ? (u.name || u.username) : (uid ? `#${uid}` : 'Unassigned');
  };
  // Roles for team-pool dropdown (TL / SM)
  const managers = users.filter(u => ['team_leader', 'sales_manager', 'branch_admin'].includes(u.role_slug));

  // Status filter chips
  const filteredRules = useMemo(() => rules.filter(r =>
    statusFilter === 'All'
    || (statusFilter === 'Active' && r.is_active)
    || (statusFilter === 'Inactive' && !r.is_active)
  ), [rules, statusFilter]);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('recycle-rules');
      if (res?.success) setRules(res.data?.rules || []);
    } catch (_) { /* handled by apiFetch */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
    fetchUsers().then(res => {
      const list = res?.success ? (Array.isArray(res.data) ? res.data : res.data?.users || []) : [];
      setUsers(list);
    });
    // Include recycle-only statuses so they can be picked as dump/new status.
    apiFetch('lead-statuses?include_recycle=1').then(res => {
      if (res?.success) setStatuses(res.data?.statuses || []);
    });
  }, [loadRules, fetchUsers]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = async (rule) => {
    setEditingId(rule.rr_id);
    const isMin = rule.duration_min % 60 !== 0;
    setForm({
      rule_name: rule.rule_name || '',
      trigger_status: (rule.trigger_lsids || []).map(Number),
      duration_value: isMin ? rule.duration_min : rule.duration_min / 60,
      duration_unit: isMin ? 'min' : 'hrs',
      new_status_lsid: rule.new_status_lsid,
      dump_status_lsid: rule.dump_status_lsid,
      rotation_mode: rule.rotation_mode,
      auto_assign: !!rule.auto_assign,
      include_followup: !!rule.include_followup,
      reset_activity_min: rule.reset_activity_min || 0,
      max_rotations: rule.max_rotations || 0,
      cooldown_min: rule.cooldown_min || 0,
      pool_mode: rule.pool_mode || 'users',
      reports_to_uid: rule.reports_to_uid || '',
      users: (rule.users || []).map(u => u.u_id),
      is_active: !!rule.is_active,
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (form.trigger_status.length === 0) return showToast('Select at least one trigger status', 'error');
    if (!form.new_status_lsid) return showToast('Select the status after rotation', 'error');
    if (!form.dump_status_lsid) return showToast('Select the dump status', 'error');

    const duration_min = form.duration_unit === 'hrs' ? Number(form.duration_value) * 60 : Number(form.duration_value);
    const payload = {
      rule_name: form.rule_name,
      trigger_status: form.trigger_status,
      duration_min,
      new_status_lsid: form.new_status_lsid,
      dump_status_lsid: form.dump_status_lsid,
      rotation_mode: form.rotation_mode,
      auto_assign: form.auto_assign ? 1 : 0,
      include_followup: form.include_followup ? 1 : 0,
      reset_activity_min: Number(form.reset_activity_min) || 0,
      max_rotations: Number(form.max_rotations) || 0,
      cooldown_min: Number(form.cooldown_min) || 0,
      pool_mode: form.pool_mode,
      reports_to_uid: form.reports_to_uid || null,
      users: form.users.map(u_id => ({ u_id })),
      is_active: form.is_active ? 1 : 0,
    };

    setSubmitting(true);
    try {
      const res = editingId
        ? await apiFetch(`recycle-rules/${editingId}`, { method: 'PUT', body: JSON.stringify(payload) })
        : await apiFetch('recycle-rules', { method: 'POST', body: JSON.stringify(payload) });
      if (res?.success) {
        showToast(editingId ? 'Rule updated' : 'Rule created', 'success');
        setShowForm(false);
        loadRules();
      } else {
        showToast(res?.message || 'Failed to save rule', 'error');
      }
    } catch (e) {
      showToast(e.message || 'Failed to save rule', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (rule) => {
    const res = await apiFetch(`recycle-rules/${rule.rr_id}/toggle`, {
      method: 'PATCH', body: JSON.stringify({ is_active: rule.is_active ? 0 : 1 }),
    });
    if (res?.success) loadRules();
    else showToast(res?.message || 'Failed', 'error');
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.rule_name || 'Untitled'}"?`)) return;
    const res = await apiFetch(`recycle-rules/${rule.rr_id}`, { method: 'DELETE' });
    if (res?.success) { showToast('Rule deleted', 'success'); loadRules(); }
    else showToast(res?.message || 'Failed to delete', 'error');
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await apiFetch('recycle-rules/run-now', { method: 'POST' });
      if (res?.success) {
        const totals = (res.data?.rules || []).reduce((a, r) => ({
          rotated: a.rotated + (r.rotated || 0), dumped: a.dumped + (r.dumped || 0),
          unassigned: a.unassigned + (r.unassigned || 0),
        }), { rotated: 0, dumped: 0, unassigned: 0 });
        showToast(`Engine ran — rotated ${totals.rotated}, dumped ${totals.dumped}, unassigned ${totals.unassigned}`, 'success');
      } else showToast(res?.message || 'Engine failed', 'error');
    } finally {
      setRunning(false);
    }
  };

  const openDetail = (rule) => navigate(`/recycle-leads/${rule.rr_id}`);

  const fmtWait = (min) => min % 60 === 0 ? `${min / 60}h` : `${min}m`;

  const renderCard = (r) => (
    <div key={r.rr_id} className="rc-card">
      <div className="rc-card__top">
        <strong className="rc-card__name">{r.rule_name || `Rule #${r.rr_id}`}</strong>
        <span className={`rc-status ${r.is_active ? 'rc-status--on' : 'rc-status--off'}`}>
          <span className="rc-status__dot" />
          {r.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="rc-card__flow">
        {(r.trigger_lsids || []).map(statusName).join(', ') || '—'}
        <span className="rc-arrow">→</span>
        {statusName(r.new_status_lsid)}
      </div>

      <div className="rc-card__meta">
        <div className="rc-card__cell"><span>Wait</span><b>{fmtWait(r.duration_min)}</b></div>
        <div className="rc-card__cell">
          <span>Mode</span>
          {r.auto_assign
            ? <Badge variant="info" className="rc-mode-badge">{r.rotation_mode}</Badge>
            : <Badge variant="warning" className="rc-mode-badge">unassign</Badge>}
        </div>
        <div className="rc-card__cell">
          <span>Pool</span>
          <b>{r.pool_mode === 'team' ? `Team: ${userName(r.reports_to_uid)}` : `${(r.users || []).length} user${(r.users || []).length === 1 ? '' : 's'}`}</b>
        </div>
      </div>

      <div className="rc-card__actions">
        <button className="rc-text-btn" onClick={() => openDetail(r)}><Eye size={15} /> View</button>
        <button className="rc-text-btn" onClick={() => openEdit(r)}><Pencil size={15} /> Edit</button>
        <button className="rc-text-btn" onClick={() => handleToggle(r)}><Power size={15} /> {r.is_active ? 'Disable' : 'Enable'}</button>
        <button className="rc-text-btn rc-text-btn--danger" onClick={() => handleDelete(r)}><Trash2 size={15} /> Delete</button>
      </div>
    </div>
  );

  return (
    <>
      <Header
        title="Recycle Leads"
        subtitle="Auto-rotate idle leads between team members"
      />
      <div className="page">
      {loading ? (
        <div className="rc-loading">Loading rules…</div>
      ) : rules.length === 0 ? (
        <EmptyState icon={Recycle} title="No recycle rules yet"
          description="Create a rule to auto-rotate leads that sit idle in a status."
          action={<Button variant="gold" icon={Plus} onClick={openCreate}>New Rule</Button>} />
      ) : (
        <>
          <div className="rc-toolbar">
            <div className="rc-seg">
              {['All', 'Active', 'Inactive'].map(s => (
                <button key={s} className={`rc-seg__btn ${statusFilter === s ? 'rc-seg__btn--active' : ''}`} onClick={() => setStatusFilter(s)}>
                  {s}
                  <span className="rc-seg__count">{s === 'All' ? rules.length : rules.filter(r => s === 'Active' ? r.is_active : !r.is_active).length}</span>
                </button>
              ))}
            </div>
            <div className="rc-toolbar-actions">
              <Button variant="ghost" icon={running ? RefreshCw : Play} onClick={handleRunNow} disabled={running}>
                {running ? 'Running…' : 'Run now'}
              </Button>
              <Button variant="gold" icon={Plus} onClick={openCreate}>New Rule</Button>
            </div>
          </div>

          {filteredRules.length === 0 ? (
            <div className="rc-no-match">
              <div className="rc-no-match__icon"><Inbox size={30} /></div>
              <h3>No {statusFilter.toLowerCase()} rules</h3>
              <p>There are no rules in this filter right now.</p>
              <Button variant="secondary" size="sm" onClick={() => setStatusFilter('All')}>Show all rules</Button>
            </div>
          ) : (
            <div className="rc-cards">
              {filteredRules.map(renderCard)}
            </div>
          )}
        </>
      )}

      {/* Create / Edit Modal */}
      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Recycle Rule' : 'New Recycle Rule'} size="lg">
        <FormInput label="Rule name" value={form.rule_name}
          onChange={e => setForm(f => ({ ...f, rule_name: e.target.value }))} placeholder="e.g. New leads rotation" />

        <MultiSelect label="Trigger statuses *" options={statusOptions} value={form.trigger_status}
          onChange={v => setForm(f => ({ ...f, trigger_status: v }))} placeholder="Statuses the rule watches" />

        <div className="rc-row">
          <FormInput label="Wait duration *" type="number" min="1" value={form.duration_value}
            onChange={e => setForm(f => ({ ...f, duration_value: e.target.value }))} />
          <FormSelect label="Unit" value={form.duration_unit} placeholder={null}
            options={[{ value: 'hrs', label: 'Hours' }, { value: 'min', label: 'Minutes' }]}
            onChange={e => setForm(f => ({ ...f, duration_unit: e.target.value }))} />
        </div>

        <div className="rc-row">
          <FormSelect label="Status after rotation *" value={form.new_status_lsid} options={statusOptions}
            onChange={e => setForm(f => ({ ...f, new_status_lsid: e.target.value }))} />
          <FormSelect label="Dump status (pool exhausted) *" value={form.dump_status_lsid} options={statusOptions}
            onChange={e => setForm(f => ({ ...f, dump_status_lsid: e.target.value }))} />
        </div>

        <label className="rc-check">
          <input type="checkbox" checked={form.auto_assign}
            onChange={e => setForm(f => ({ ...f, auto_assign: e.target.checked }))} />
          Auto-assign to next user (off = just unassign)
        </label>

        {form.auto_assign && (
          <>
            <FormSelect label="Rotation mode" value={form.rotation_mode} options={ROTATION_MODES} placeholder={null}
              onChange={e => setForm(f => ({ ...f, rotation_mode: e.target.value }))} />

            <FormSelect label="Pool type" value={form.pool_mode} placeholder={null}
              options={[{ value: 'users', label: 'Specific users' }, { value: 'team', label: 'Whole team (by leader)' }]}
              onChange={e => setForm(f => ({ ...f, pool_mode: e.target.value }))} />

            {form.pool_mode === 'users' ? (
              <MultiSelect label="Rotation pool (users)" value={form.users}
                options={users.map(u => ({ value: u.u_id, label: u.name || u.username }))}
                onChange={v => setForm(f => ({ ...f, users: v }))} placeholder="Pick users" />
            ) : (
              <FormSelect label="Team leader / manager" value={form.reports_to_uid}
                options={managers.map(u => ({ value: u.u_id, label: `${u.name || u.username} (${u.role_name})` }))}
                onChange={e => setForm(f => ({ ...f, reports_to_uid: e.target.value }))} />
            )}
          </>
        )}

        <div className="rc-row">
          <FormInput label="Reset if active in last (min, 0=off)" type="number" min="0" value={form.reset_activity_min}
            onChange={e => setForm(f => ({ ...f, reset_activity_min: e.target.value }))} />
          <FormInput label="Max rotations (0=unlimited)" type="number" min="0" value={form.max_rotations}
            onChange={e => setForm(f => ({ ...f, max_rotations: e.target.value }))} />
          <FormInput label="Cooldown (min, 0=off)" type="number" min="0" value={form.cooldown_min}
            onChange={e => setForm(f => ({ ...f, cooldown_min: e.target.value }))} />
        </div>

        <label className="rc-check">
          <input type="checkbox" checked={form.include_followup}
            onChange={e => setForm(f => ({ ...f, include_followup: e.target.checked }))} />
          Also rotate leads that have a future follow-up
        </label>
        <label className="rc-check">
          <input type="checkbox" checked={form.is_active}
            onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
          Rule active
        </label>

        <div className="rc-modal-actions">
          <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}>{submitting ? 'Saving…' : (editingId ? 'Update' : 'Create')}</Button>
        </div>
      </Modal>
      </div>
    </>
  );
};

export default RecycleLeads;
